import * as React from "react";
import {useCallback, useMemo} from "react";
import {
   EnumBrandItemAttributesApparentFabricType,
   EnumBrandItemAttributesPrimaryColor,
   EnumBrandItemAttributesSeasonSuitability,
   EnumBrandItemAttributesType,
   EnumBrandItemItemState,
   FilterFindManyBrandItemInput,
   FlexBrandItemFragment,
   SortFindManyBrandItemInput,
   useBrandItemsBrandSettingsQuery,
   useBrandItemsListManyLazyQuery,
   useBrandItemsListByIdsLazyQuery
} from "../../Queries";
import {BrandItemColumnsProps, getBrandItemColumns} from "./BrandItemsListColumns";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../atoms/CurrentUser";
import {RangeList} from "../../components/rangelist/RangeList";
import {RangeCommand, RangeFilter, RangeSort, RangeTab, RangeListColumn} from "../../components/rangelist/RangeListTypes";
import {dataViewColumnIds, dataViewRangeColumns} from "../../components/dataview/DataViewColumns";
import {useDataView} from "../../components/dataview/useDataView";


type HeaderOptionsProps = {
   hide?: boolean
}

type BrandItemShop = {
   _id: string
   name: string
}

type BrandItemsListProps = {
   listId?: string
   brandHouseId?: string
   currency?: string
   headerOptions?: HeaderOptionsProps
   columnOptions?: BrandItemColumnsProps
   onSelectItem?: (brandItem: FlexBrandItemFragment, newWindow?: boolean) => void
   commands?: RangeCommand<FlexBrandItemFragment>[]
   filter?: any
   shops?: BrandItemShop[]
   viewId?: string
}

const PAGE_SIZE = 50;

export const BrandItemsList = ({
                                  listId,
                                  brandHouseId,
                                  columnOptions,
                                  onSelectItem,
                                  commands,
                                  headerOptions,
                                  filter: extraFilter,
                                  shops,
                                  viewId
                               }: BrandItemsListProps) => {
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);

   const {data} = useBrandItemsBrandSettingsQuery({
      variables: {
         brandHouseId
      }
   })
   const [fetchMore] = useBrandItemsListManyLazyQuery({
      fetchPolicy: "no-cache"
   });

   const [fetchByIds] = useBrandItemsListByIdsLazyQuery({
      fetchPolicy: "no-cache"
   });

   const {annotations, loading: viewLoading} = useDataView(viewId);

   const columns: RangeListColumn<FlexBrandItemFragment>[] = useMemo(() => {
      const itemColumns = getBrandItemColumns(columnOptions || {});
      if (!annotations) {
         return itemColumns;
      }
      // The view's own columns read best right next to the product they describe
      const viewColumns = dataViewRangeColumns<FlexBrandItemFragment>(annotations);
      const titleIndex = itemColumns.findIndex(column => column.id === 'title');
      if (titleIndex < 0) {
         return [...itemColumns, ...viewColumns];
      }
      return [...itemColumns.slice(0, titleIndex + 1), ...viewColumns, ...itemColumns.slice(titleIndex + 1)];
   }, [columnOptions, annotations])

   const sortSpec: RangeSort<SortFindManyBrandItemInput>[] = useMemo(() => {
      const sorts: RangeSort<SortFindManyBrandItemInput>[] = [{
         id: 'LocationAsc',
         label: 'Location A-Z',
         value: SortFindManyBrandItemInput.STOCKLOCATION_ASC
      }, {
         id: 'LocationDesc',
         label: 'Location Z-A',
         value: SortFindManyBrandItemInput.STOCKLOCATION_DESC
      }, {
         id: 'CreatedAsc',
         label: 'Created (oldest first)',
         value: SortFindManyBrandItemInput._ID_ASC
      }, {
         id: 'CreatedDesc',
         label: 'Created (newest first)',
         value: SortFindManyBrandItemInput._ID_DESC
      }, {
         id: 'UpdatedAsc',
         label: 'Updated (oldest first)',
         value: SortFindManyBrandItemInput.UPDATEDAT_ASC
      }, {
         id: 'UpdatedDesc',
         label: 'Updated (newest first)',
         value: SortFindManyBrandItemInput.UPDATEDAT_DESC
      }, {
         id: 'LayawayDesc',
         label: 'Layaway',
         value: SortFindManyBrandItemInput.STOCKLOCATIONTS_DESC
      }, {
         id: 'SalesRank',
         label: 'Sales Rank',
         value: SortFindManyBrandItemInput.SALESSCORE_DESC
      }];

      // Add attribute sorts
      sorts.push({
         id: 'SeasonSuitabilityAsc',
         label: 'Season Suitability A-Z',
         value: SortFindManyBrandItemInput.ATTRIBUTES__SEASONSUITABILITY_ASC
      }, {
         id: 'SeasonSuitabilityDesc',
         label: 'Season Suitability Z-A',
         value: SortFindManyBrandItemInput.ATTRIBUTES__SEASONSUITABILITY_DESC
      });

      // Add brandhouse-prefixed sorts when viewing multiple brands
      if (columnOptions?.brand) {
         sorts.push({
            id: 'BrandPrimaryColorAsc',
            label: 'Brand > Primary Color A-Z',
            value: SortFindManyBrandItemInput.BRANDHOUSEID__ATTRIBUTES__PRIMARYCOLOR_ASC
         }, {
            id: 'BrandPrimaryColorDesc',
            label: 'Brand > Primary Color Z-A',
            value: SortFindManyBrandItemInput.BRANDHOUSEID__ATTRIBUTES__PRIMARYCOLOR_DESC
         }, {
            id: 'BrandTypeAsc',
            label: 'Brand > Type A-Z',
            value: SortFindManyBrandItemInput.BRANDHOUSEID__ATTRIBUTES__TYPE_ASC
         }, {
            id: 'BrandTypeDesc',
            label: 'Brand > Type Z-A',
            value: SortFindManyBrandItemInput.BRANDHOUSEID__ATTRIBUTES__TYPE_DESC
         });
      }

      return sorts;
   }, [columnOptions?.brand])

   const filterSpec: RangeFilter<FilterFindManyBrandItemInput>[] = useMemo(() => {
      const spec: RangeFilter<FilterFindManyBrandItemInput>[] = [{
         id: 'status',
         label: 'Product state',
         options: [{
            label: 'None',
            filter: {
               itemState: EnumBrandItemItemState.NONE
            }
         }, {
            label: 'Draft',
            filter: {
               itemState: EnumBrandItemItemState.DRAFT
            }
         }, {
            label: 'Review',
            filter: {
               itemState: EnumBrandItemItemState.REVIEW
            }
         }, {
            label: 'Ready',
            filter: {
               itemState: EnumBrandItemItemState.READY
            }
         }, {
            label: 'Queued',
            subtext: 'Published, but waiting behind an identical item that is for sale',
            filter: {
               queued: true
            }
         }]
      }, {
         id: 'problems',
         label: 'Problems',
         options: [{
            label: 'Problems (Yes)',
            filter: {
               hasProblems: true
            }
         }, {
            label: 'Problems (No)',
            filter: {
               hasProblems: false
            }
         }]
      }, {
         id: 'missing',
         label: 'Missing fields',
         options: [{
            label: 'Missing fields (Yes)',
            filter: {
               hasErrors: true
            }
         }, {
            label: 'Missing fields (No)',
            filter: {
               hasErrors: false
            }
         }]
      }, {
         id: 'masterdata',
         label: 'Masterdata',
         options: [{
            label: 'Masterdata (Yes)',
            filter: {
               hasMasterData: true
            }
         }, {
            label: 'Masterdata (No)',
            filter: {
               hasMasterData: false
            }
         }]
      }, {
         id: 'stocklocation',
         label: 'Has stocklocation',
         options: [{
            label: 'Has stock location',
            filter: {
               hasLocation: true
            }
         }, {
            label: 'No stock location',
            filter: {
               hasLocation: false
            }
         }]
      }, {
         id: 'images',
         label: 'Has required images',
         options: [{
            label: 'Yes',
            filter: {
               hasCustomImages: true
            }
         }, {
            label: 'No',
            filter: {
               hasCustomImages: false
            }
         }]
      }, {
         id: 'testing',
         label: 'Is test',
         options: [{
            label: 'Yes',
            filter: {
               test: true
            }
         }, {
            label: 'No',
            filter: {
               test: false
            }
         }]
      }, {
         id: 'manufacturer',
         label: 'Valid vendor',
         options: [{
            label: 'Yes',
            filter: {
               invalidManufacturer: false
            }
         }, {
            label: 'No',
            filter: {
               invalidManufacturer: true
            }
         }]
      }, {
         id: 'sold',
         label: 'Sold',
         options: [{
            label: 'Yes',
            filter: {
               isSold: true
            }
         }, {
            label: 'No',
            filter: {
               isSold: false
            }
         }]
      }, {
         id: 'tradeIn',
         label: 'Trade In',
         options: [{
            label: 'Yes',
            filter: {
               isTradeIn: true
            }
         }, {
            label: 'No',
            filter: {
               isTradeIn: false
            }
         }]
      }, {
         id: 'available',
         label: 'Item is available',
         options: [{
            label: 'Yes',
            filter: {
               unavailable: null,
               _operators: {
                  missing: {
                     exists: false
                  }
               }
            }
         }]
      }, {
         id: 'discarded',
         label: 'Item is discarded',
         options: [{
            label: 'Yes',
            filter: {
               _operators: {
                  discarded: {
                     exists: true
                  }
               }
            }
         }, {
            // `discarded` is a date, so "not discarded" is the unset case. A null
            // equality match covers both a missing field and an explicit null.
            label: 'No',
            filter: {
               discarded: null
            }
         }]
      }];

      if (columnOptions?.brand) {
         spec.push({
            id: 'brand',
            label: 'Brand',
            options: data?.BrandHouses?.map(brand => ({
               label: brand?.name || '',
               filter: {
                  brandHouseId: brand?._id
               }
            })) || []
         })
      }

      if (data?.BrandSettings?.tradeInComments) {
         spec.push(...[{
            id: 'comments',
            label: 'Has comments',
            options: [{
               label: 'Has comments',
               filter: {
                  hasComment: true
               }
            }, {
               label: 'No comments',
               filter: {
                  hasComment: false
               }
            }]
         }, {
            id: 'approvedComments',
            label: 'Approved comments',
            options: [{
               label: 'Approved comments',
               filter: {
                  commentApproved: true
               }
            }, {
               label: 'Unapproved comments',
               filter: {
                  commentApproved: false
               }
            }]
         }, {
            id: 'commentsTranslated',
            label: 'Translated comments',
            options: [{
               label: 'Translated comments',
               filter: {
                  hasTranslatedComment: true
               }
            }, {
               label: 'Non translated comments',
               filter: {
                  hasTranslatedComment: false
               }
            }]
         }])
      }


      if(isAdministrator) {
         spec.push({
            id: 'attributeType',
            label: 'AI:Type',
            options: Object.values(EnumBrandItemAttributesType).map(itemType => ({
               label: (itemType || '').replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
               filter: {
                  attributes: {
                     type: itemType
                  }
               }
            })) || []
         });
         spec.push({
            id: 'attributeColor',
            label: 'AI:Primary Color',
            options: Object.values(EnumBrandItemAttributesPrimaryColor).map(color => ({
               label: (color || '').replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
               filter: {
                  attributes: {
                     primaryColor: color
                  }
               }
            })) || []
         });
         spec.push({
            id: 'attributeFabric',
            label: 'AI:Fabric Type',
            options: Object.values(EnumBrandItemAttributesApparentFabricType).map(fabric => ({
               label: (fabric || '').replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
               filter: {
                  attributes: {
                     apparentFabricType: fabric
                  }
               }
            })) || []
         });
         spec.push({
            id: 'attributeSeason',
            label: 'AI:Season',
            options: Object.values(EnumBrandItemAttributesSeasonSuitability).map(season => ({
               label: (season || '').replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
               filter: {
                  attributes: {
                     seasonSuitability: season
                  }
               }
            })) as any || []
         });
      }


      return spec;
   }, [data, columnOptions]);

   const tabsSpec: RangeTab[] = useMemo(() => {
      // If viewId is provided, only show the "All" tab
      if (viewId) {
         const columnIds = ['brand', 'id', 'image', 'title', 'sku', 'size', 'color', 'beforePrice', 'boost', 'stockLocation', 'status', 'shops', 'createdAt'];
         const viewColumnIds = annotations ? dataViewColumnIds(annotations) : [];
         const titleIndex = columnIds.indexOf('title');
         return [{
            id: 'All',
            label: 'All',
            filters: [],
            columns: [...columnIds.slice(0, titleIndex + 1), ...viewColumnIds, ...columnIds.slice(titleIndex + 1)],
            // The view's columns are the point of the drilldown, so a saved column selection must not hide them
            columnsForced: viewColumnIds
         }];
      }

      // Otherwise, build the normal tabs
      const tabs: RangeTab[] = [{
         id: 'Sale',
         label: 'Ready',
         filters: [{
            filterId: 'status',
            values: ['Ready']
         }, {
            filterId: 'sold',
            values: ['No']
         }, {
            filterId: 'missing',
            values: ['No']
         }, {
            filterId: 'problems',
            values: ['Problems (No)']
         }, {
            filterId: 'available',
            values: ['Yes']
         }],
         columns: ['brand', 'id', 'image', 'title', 'beforePrice', 'boost', 'activeshops', 'status']
      }, {
         id: 'Review',
         label: 'Need review',
         filters: [{
            filterId: 'status',
            values: ['Review']
         }, {
            filterId: 'sold',
            values: ['No']
         }, {
            filterId: 'problems',
            values: ['Problems (No)']
         }, {
            filterId: 'available',
            values: ['Yes']
         }],
         showCount: true,
         columns: ['brand', 'id', 'image', 'title', 'reviewStatus']
      }, {
         id: 'Sold',
         label: 'Sold',
         filters: [{
            filterId: 'sold',
            values: ['Yes']
         }],
         columns: ['brand', 'id', 'image', 'title', 'sku', 'beforePrice', 'status']
      }];

      if (isAdministrator) {
         // Add additional tabs for admin users
         tabs.push({
            id: 'All',
            label: 'All',
            filters: [],
            columns: ['brand', 'id', 'image', 'title', 'sku', 'size', 'color', 'beforePrice', 'boost', 'stockLocation', 'status', 'shops', 'createdAt']
         });

         // Insert after the first tab
         tabs.splice(2, 0, ...[{
            id: 'readyForReview',
            label: 'Move to review',
            filters: [{
               filterId: 'images',
               values: ['Yes']
            }, {
               filterId: 'testing',
               values: ['No']
            }, {
               filterId: 'stocklocation',
               values: ['Has stock location']
            }, {
               filterId: 'problems',
               values: ['Problems (No)']
            }, {
               filterId: 'sold',
               values: ['No']
            }, {
               filterId: 'status',
               values: ['None', 'Draft']
            }, {
               filterId: 'available',
               values: ['Yes']
            }],
            showCount: true,
            columns: ['brand', 'id', 'image', 'title', 'reviewStatus']
         }, {
            id: 'readyForSale',
            label: 'Ready for sale',
            filters: [{
               filterId: 'images',
               values: ['Yes']
            }, {
               filterId: 'testing',
               values: ['No']
            }, {
               filterId: 'stocklocation',
               values: ['Has stock location']
            }, {
               filterId: 'missing',
               values: ['Missing fields (No)']
            }, {
               filterId: 'problems',
               values: ['Problems (No)']
            }, {
               filterId: 'sold',
               values: ['No']
            }, {
               filterId: 'status',
               values: ['None', 'Draft', 'Review']
            }, {
               filterId: 'available',
               values: ['Yes']
            }],
            showCount: true,
            columns: ['brand', 'id', 'image', 'title', 'sku', 'beforePrice', 'originalEan', 'size', 'color', 'stockLocation', 'shops']
         }, {
            id: 'takeAction',
            label: 'Take action',
            filters: [{
               filterId: 'problems',
               values: ['Problems (Yes)']
            }, {
               filterId: 'sold',
               values: ['No']
            }, {
               filterId: 'available',
               values: ['Yes']
            }],
            showCount: true,
            columns: ['brand', 'id', 'imageExtended', 'title', 'sku', 'stockLocation', 'takeAction']
         }]);
      }

      if (data?.BrandSettings?.tradeInComments) {
         const commentTab = {
            id: 'comments',
            label: 'Comments',
            filters: [{
               filterId: 'comments',
               values: ['Has comments']
            }],
            showCount: true,
            columns: ['brand', 'id', 'image', 'title', 'comment', 'commentTranslated', 'commentApproved']
         }
         tabs.splice(2, 0, ...[commentTab]);
      }

      return tabs.map(tab => tab.id === 'All' ? tab : {
         ...tab,
         filters: [...tab.filters, {filterId: 'discarded', values: ['No']}]
      });
   }, [viewId, annotations, isAdministrator, data?.BrandSettings?.tradeInComments]);

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter?: any) => {
      const effectiveFilter = Object.assign({}, filter, extraFilter)
      const {data} = await fetchMore({
         variables: {
            page: page,
            sort: (sort as SortFindManyBrandItemInput) || SortFindManyBrandItemInput._ID_DESC,
            perPage: perPage,
            search: search,
            filter: {
               brandHouseId: brandHouseId,
               ...effectiveFilter
            },
            viewId: viewId
         }
      });

      const resultData = data?.BrandItemPagination?.items?.map(item => item!.resolved!) || []
      return {
         data: resultData,
         totalCount: data?.BrandItemPagination?.count || 0
      }
   }, [brandHouseId, extraFilter, fetchMore, viewId]);

   const onSelectAll = useCallback(async (filter?: any): Promise<string[]> => {
      const effectiveFilter = Object.assign({}, filter, extraFilter);
      // Fetch all IDs matching the current filter - use a large perPage to get all at once
      // We only need the _id field, so we fetch minimal data
      const {data} = await fetchMore({
         variables: {
            page: 1,
            sort: SortFindManyBrandItemInput._ID_DESC,
            perPage: 10000, // Large number to get all IDs
            search: undefined,
            filter: {
               brandHouseId: brandHouseId,
               ...effectiveFilter
            },
            viewId: viewId
         }
      });

      return data?.BrandItemPagination?.items?.map(item => item!.resolved!._id) || [];
   }, [brandHouseId, extraFilter, fetchMore, viewId]);

   const onReloadRows = useCallback(async (ids: string[]): Promise<FlexBrandItemFragment[]> => {
      const {data} = await fetchByIds({
         variables: {
            ids: ids
         }
      });
      return data?.BrandItems?.map((item:any) => item!.resolved!).filter(Boolean) as FlexBrandItemFragment[] || [];
   }, [fetchByIds]);

   const locales = data?.BrandSettings?.locales?.filter(loc => loc?.activeItems).map(loc => loc!.locale);
   const currencies = data?.BrandSettings?.stockItemConfig?.currencies?.filter(loc => loc?.active).map(loc => loc!.currency)

   // RangeList seeds its column catalogue once on mount, so the view's columns must be known before it renders
   if (viewLoading) {
      return null;
   }

   return <RangeList
      selections={true}
      globalId={listId || "brandItems"}
      tabs={tabsSpec}
      itemsPerPage={PAGE_SIZE}
      columns={columns}
      onRequestData={onRequestData}
      onSelectAll={onSelectAll}
      filters={filterSpec}
      sorts={sortSpec}
      searchLabel={"Search"}
      commands={commands}
      context={{
         brandHouseId: brandHouseId,
         currency: currencies?.[0],
         locale: locales?.[0],
         locales: locales,
         currencies: currencies,
         shops: shops
      }}
      onSelectRow={onSelectItem}
      onReloadRows={onReloadRows}
   />
}
